'use client';

import { useState } from 'react';
import styles from './MeasurementPlan.module.scss';
import pageStyles from '@/src/app/[lang]/north-star-dashboard/page.module.scss';
import { Check, Coffee, Loader2, Mail, X } from 'lucide-react';

interface LeadFormProps {
  planTitle: string;
  planMarkdown: string;
  isEn: boolean;
  lang: 'fr' | 'en';
  isOpen: boolean;
  onClose: () => void;
  onTrack: (type: 'mvp_plan_email_submitted') => void;
}

export function LeadForm({
  planTitle,
  planMarkdown,
  isEn,
  lang,
  isOpen,
  onClose,
  onTrack,
}: LeadFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const labels = {
    title: isEn ? 'Want to go deeper?' : 'Envie d’approfondir ?',
    subtitle: isEn
      ? 'Leave your details if you’d like to discuss this plan over a coffee.'
      : 'Laissez vos coordonnées si vous souhaitez qu’on en discute autour d’un café.',
    optional: isEn ? 'This step is optional.' : 'Cette étape est optionnelle.',
    nameLabel: isEn ? 'Name' : 'Nom',
    namePlaceholder: isEn ? 'Jane Doe' : 'Marie Dupont',
    emailLabel: isEn ? 'Email' : 'Email',
    emailPlaceholder: isEn ? 'jane@company.com' : 'marie@entreprise.com',
    cta: isEn ? 'Send' : 'Envoyer',
    skip: isEn ? 'Skip for now' : 'Passer pour l’instant',
    sending: isEn ? 'Sending...' : 'Envoi...',
    success: isEn
      ? 'Message sent. We’ll be in touch soon.'
      : 'Message envoyé. On vous recontacte vite.',
    thanks: isEn ? 'Thank you!' : 'Merci !',
    privacy: isEn ? 'No spam. Human review available.' : 'Pas de spam. Revue humaine disponible.',
  };

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();

    // Nothing to send: just close
    if (!trimmedEmail && !trimmedName) {
      onClose();
      return;
    }

    // Email is invalid but name is present: we can still send the lead with name only
    // If only email is present, it must be valid
    if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
      return;
    }

    setStatus('sending');
    try {
      const res = await fetch('/api/mvp-measurement-plan/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          name: trimmedName,
          lang,
          planTitle,
          planMarkdown,
        }),
      });

      if (res.ok) {
        setStatus('sent');
        onTrack('mvp_plan_email_submitted');
        setTimeout(() => {
          onClose();
          setStatus('idle');
        }, 2000);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className={pageStyles.leadModalOverlay} data-testid="lead-modal">
      <div className={pageStyles.leadModal} role="dialog" aria-modal="true">
        <button
          type="button"
          className={pageStyles.leadModalClose}
          onClick={onClose}
          aria-label={isEn ? 'Close' : 'Fermer'}
        >
          <X size={18} />
        </button>

        {status === 'sent' ? (
          <div className={pageStyles.leadModalSuccess}>
            <div className={pageStyles.leadModalSuccessIcon}>
              <Check size={32} />
            </div>
            <h3 className={pageStyles.leadModalTitle}>{labels.thanks}</h3>
            <p className={pageStyles.leadModalHint}>{labels.success}</p>
          </div>
        ) : (
          <>
            <div className={pageStyles.leadModalHeader}>
              <div className={pageStyles.leadModalIcon}>
                <Coffee size={24} />
              </div>
              <h3 className={pageStyles.leadModalTitle}>{labels.title}</h3>
              <p className={pageStyles.leadModalHint}>{labels.subtitle}</p>
              <p className={pageStyles.leadModalOptional}>{labels.optional}</p>
            </div>

            <div className={pageStyles.leadModalGrid}>
              <div className={pageStyles.field}>
                <label htmlFor="leadName" className={pageStyles.fieldLabel}>
                  {labels.nameLabel}
                </label>
                <input
                  id="leadName"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={labels.namePlaceholder}
                  className={pageStyles.textInput}
                />
              </div>
              <div className={pageStyles.field}>
                <label htmlFor="leadEmail" className={pageStyles.fieldLabel}>
                  {labels.emailLabel}
                </label>
                <input
                  id="leadEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={labels.emailPlaceholder}
                  className={pageStyles.textInput}
                />
              </div>
            </div>

            <div className={pageStyles.leadModalActions}>
              <button
                type="button"
                className={pageStyles.leadModalSkip}
                onClick={onClose}
                disabled={status === 'sending'}
              >
                {labels.skip}
              </button>
              <button
                type="button"
                className={styles.leadFormCta}
                disabled={status === 'sending'}
                onClick={handleSubmit}
              >
                {status === 'sending' ? (
                  <>
                    <Loader2 size={16} className={pageStyles.graphLoaderSpin} />
                    {labels.sending}
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    {labels.cta}
                  </>
                )}
              </button>
            </div>

            <div className={styles.trustBadges}>
              <span className={styles.trustBadge}>
                <Mail size={14} />
                {labels.privacy}
              </span>
            </div>

            {status === 'error' && (
              <div className={pageStyles.error}>
                {isEn ? 'Could not send. Please try again.' : 'Envoi impossible. Veuillez réessayer.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
